/**
 * RoleHomeDashboard — configurable read-leaning home for every persona
 * that doesn't have a dedicated layout. Drives KPIs + primary action
 * queue + supporting panels from a small config block per role, so
 * adding a new persona is a 15-line change.
 */
import React from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Coins,
  AlertTriangle,
  Activity,
  ClipboardList,
  Compass,
  FileCheck2,
  Briefcase,
  ChevronRight,
  Send,
} from "lucide-react";
import { DashboardDataProvider, useDashboardData } from "@/contexts/DashboardDataContext";
import { useTenantData } from "@/contexts/TenantModeContext";
import { useAuth } from "@/contexts/AuthContext";
import { Flywheel } from "@/components/brand/Flywheel";
import { SyncedIndicator } from "@/components/data/SyncedIndicator";
import { KpiTile, PanelCard, fmtAud } from "./components/DashboardPrimitives";

const ROLE_CONFIG = {
  legal: {
    eyebrow: "Compliance · Legal Officer",
    tagline: "Every approval and evidence pack the regulator will ever ask for, in one queue.",
    kpis: (ctx) => [
      kpi("My pending approvals", ctx.pendingStage3, "stage 3 sign-off", ClipboardList, "tralenex"),
      kpi("Framework coverage", `${ctx.kpis?.avgComplianceCoverage ?? 0}%`, `${ctx.frameworkCount} mapped`, ShieldCheck, "gold"),
      kpi("Signed evidence packs", `${ctx.signedReports} / ${ctx.totalReports}`, "tamper-evident", FileCheck2, "opp"),
      kpi("Open policy violations", ctx.kpis?.policyViolations30d ?? 0, "last 30 days", AlertTriangle, ctx.kpis?.policyViolations30d ? "high" : "low"),
    ],
    queueTitle: "Awaiting your Stage 3 review",
    queueLink: "/deploy/requests",
    queueEmpty: "No requests awaiting Legal sign-off right now.",
    queueFilter: (r) => r.stage === 3 && !r.terminal && r.status !== "rejected",
    sidePanels: ["evidence", "frameworks"],
  },
  cio: {
    eyebrow: "Technology · CIO",
    tagline: "Vendor health, integration posture, and platform spend at a glance.",
    kpis: (ctx) => [
      kpi("Vendors connected", ctx.vendorCount, "active integrations", Briefcase, "tralenex"),
      kpi("Monthly platform spend", fmtAud(ctx.kpis?.monthlySpendAud), `${(ctx.kpis?.roiRatio ?? 0).toFixed(1)}× ROI`, Coins, "gold"),
      kpi("Ungoverned agents", ctx.kpis?.shadowAi ?? 0, "shadow AI detected", AlertTriangle, ctx.kpis?.shadowAi ? "high" : "low"),
      kpi("Drift signals", ctx.driftWarnings, "amber + red", Activity, ctx.driftWarnings ? "gold" : "low"),
    ],
    queueTitle: "Recent deployment requests",
    queueLink: "/deploy/requests",
    queueEmpty: "No active deployment requests.",
    queueFilter: () => true,
    queueLimit: 5,
    sidePanels: ["vendors", "evidence"],
  },
  cro: {
    eyebrow: "Risk · Chief Risk Officer",
    tagline: "Concentration of risk across the AI estate — high-risk agents, open incidents, drift.",
    kpis: (ctx) => [
      kpi("High-risk agents", ctx.highRiskAgents, "of all governed", AlertTriangle, ctx.highRiskAgents > 3 ? "high" : "gold"),
      kpi("Open incidents", ctx.kpis?.incidentsOpen ?? 0, "needs triage", AlertTriangle, ctx.kpis?.incidentsOpen ? "high" : "low"),
      kpi("Drift signals", ctx.driftWarnings, "amber + red", Activity, ctx.driftWarnings ? "gold" : "low"),
      kpi("My pending approvals", ctx.pendingStage3, "stage 3 sign-off", ClipboardList, "tralenex"),
    ],
    queueTitle: "Risk-flagged requests pending your view",
    queueLink: "/deploy/requests",
    queueEmpty: "No risk-flagged requests pending.",
    queueFilter: (r) => (r.risk === "high" || r.stage === 3) && !r.terminal && r.status !== "rejected",
    sidePanels: ["frameworks", "evidence"],
  },
  itsec: {
    eyebrow: "Security · IT Security Officer",
    tagline: "Stage-2 reviews, data-residency posture, and policy enforcement.",
    kpis: (ctx) => [
      kpi("My pending reviews", ctx.pendingStage2, "stage 2 review", ClipboardList, "tralenex"),
      kpi("Policy violations", ctx.kpis?.policyViolations30d ?? 0, "last 30 days", AlertTriangle, ctx.kpis?.policyViolations30d ? "high" : "low"),
      kpi("Vendors connected", ctx.vendorCount, "all under DLP", Briefcase, "gold"),
      kpi("Ungoverned agents", ctx.kpis?.shadowAi ?? 0, "shadow AI", AlertTriangle, ctx.kpis?.shadowAi ? "high" : "low"),
    ],
    queueTitle: "Awaiting your Stage 2 review",
    queueLink: "/deploy/requests",
    queueEmpty: "No requests pending IT Security review.",
    queueFilter: (r) => r.stage === 2 && !r.terminal && r.status !== "rejected",
    sidePanels: ["vendors", "evidence"],
  },
  dslead: {
    eyebrow: "Data & AI · Data Science Lead",
    tagline: "Owned agents, performance, drift signals, and HIL queue.",
    kpis: (ctx) => [
      kpi("Governed agents", ctx.kpis?.governedAgents ?? 0, "across departments", Briefcase, "tralenex"),
      kpi("Drift signals", ctx.driftWarnings, "amber + red", Activity, ctx.driftWarnings ? "gold" : "low"),
      kpi("My pending approvals", ctx.pendingStage5plus, "stage 5–6 ship", ClipboardList, "tralenex"),
      kpi("Open incidents", ctx.kpis?.incidentsOpen ?? 0, "needs triage", AlertTriangle, ctx.kpis?.incidentsOpen ? "high" : "low"),
    ],
    queueTitle: "Awaiting your sign-off (Stages 5–6)",
    queueLink: "/deploy/requests",
    queueEmpty: "No requests awaiting DS Lead sign-off.",
    queueFilter: (r) => (r.stage === 5 || r.stage === 6) && !r.terminal && r.status !== "rejected",
    sidePanels: ["vendors", "frameworks"],
  },
  buhead: {
    eyebrow: "Lines of Business · Department Head",
    tagline: "Opportunities you've sponsored and their progress through governance.",
    kpis: (ctx) => [
      kpi("Opportunities sponsored", ctx.myOpportunities, "this quarter", Compass, "tralenex"),
      kpi("In-flight requests", ctx.myInflight, "across stages 1–6", ClipboardList, "gold"),
      kpi("Deployed agents", ctx.myDeployedAgents, "live in production", Briefcase, "opp"),
      kpi("Estimated value delivered", fmtAud(ctx.myDeliveredValue), "annualised", Coins, "low"),
    ],
    queueTitle: "Your active requests",
    queueLink: "/deploy/requests",
    queueEmpty: "You haven't submitted any active requests recently.",
    queueFilter: (r, user) => r.requestor === user.name && !r.terminal,
    sidePanels: ["frameworks"],
  },
};

function kpi(label, value, hint, icon, tone) {
  return { label, value, hint, icon, tone };
}

export default function RoleHomeDashboard({ roleId }) {
  return (
    <DashboardDataProvider>
      <RoleHomeInner roleId={roleId} />
    </DashboardDataProvider>
  );
}

function RoleHomeInner({ roleId }) {
  const { tenant, kpis, frameworks, reports, loading, error, lastSyncedAt, refetch } = useDashboardData();
  const { requests, agents, opportunities, vendors, driftSignals } = useTenantData();
  const { user } = useAuth();
  const cfg = ROLE_CONFIG[roleId];
  if (!cfg) return <RoleError message={`No config for role "${roleId}".`} />;

  if (loading && !tenant) return <RoleSkeleton />;
  if (error) return <RoleError message={error.message} />;

  const ctx = {
    kpis,
    frameworkCount: (frameworks || []).length,
    signedReports: (reports || []).filter((r) => r.signed).length,
    totalReports: (reports || []).length,
    vendorCount: (vendors || []).length,
    pendingStage2: (requests || []).filter((r) => r.stage === 2 && !r.terminal).length,
    pendingStage3: (requests || []).filter((r) => r.stage === 3 && !r.terminal).length,
    pendingStage5plus: (requests || []).filter((r) => (r.stage === 5 || r.stage === 6) && !r.terminal).length,
    highRiskAgents: (agents || []).filter((a) => a.riskBand === "high").length,
    driftWarnings: (driftSignals || []).filter((d) => d.status === "warning" || d.status === "alert").length,
    myOpportunities: user ? (opportunities || []).filter((o) => o.owner === user.name || o.owner === user.persona?.role).length : 0,
    myInflight: user ? (requests || []).filter((r) => r.requestor === user.name && !r.terminal).length : 0,
    myDeployedAgents: user ? (agents || []).filter((a) => a.ownerName === user.name).length : 0,
    myDeliveredValue: user
      ? (agents || []).filter((a) => a.ownerName === user.name).reduce((s, a) => s + (a.monthlyValueAud || 0) * 12, 0)
      : 0,
  };

  const queueItems = (requests || [])
    .filter((r) => cfg.queueFilter(r, user))
    .slice(0, cfg.queueLimit || 6);

  return (
    <div className="relative px-6 lg:px-8 py-6 anim-fade-up" data-testid={`role-home-${roleId}`}>
      <div className="absolute -right-24 -top-12 pointer-events-none">
        <Flywheel size={300} opacity={0.04} />
      </div>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="eyebrow">{cfg.eyebrow}</p>
          <h1 className="text-display-md text-ink-primary mt-1">
            Good day, {user?.name?.split(" ")[0] || "there"}.
          </h1>
          <p className="font-ceremonial italic text-ink-secondary text-[17px] mt-1 max-w-2xl">
            {cfg.tagline}
          </p>
          <p className="text-meta text-ink-disabled mt-2">
            {tenant?.name} · {tenant?.industry} · view as of {new Date().toISOString().slice(0, 10)}
          </p>
        </div>
        <SyncedIndicator lastSyncedAt={lastSyncedAt} loading={loading} onRefetch={refetch} testId={`role-home-${roleId}-synced`} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        {cfg.kpis(ctx).map((k) => (
          <KpiTile
            key={k.label}
            label={k.label}
            value={k.value}
            delta={k.hint}
            deltaTone="neutral"
            icon={k.icon}
            tone={k.tone}
            testId={`role-kpi-${roleId}-${k.label.replace(/\s+/g, "-").toLowerCase()}`}
          />
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
        <div className="lg:col-span-2">
          <PanelCard
            title={cfg.queueTitle}
            action={
              <Link to={cfg.queueLink} className="text-meta font-semibold text-tralenex-700 hover:text-tralenex-800 inline-flex items-center gap-0.5">
                Open requests <ChevronRight size={11} />
              </Link>
            }
            testId={`role-home-${roleId}-queue`}
          >
            {queueItems.length === 0 ? (
              <p className="text-meta text-ink-secondary">{cfg.queueEmpty}</p>
            ) : (
              <ul className="space-y-2">
                {queueItems.map((r) => (
                  <QueueRow key={r.id} request={r} />
                ))}
              </ul>
            )}
          </PanelCard>
        </div>
        <div className="flex flex-col gap-4">
          {cfg.sidePanels.includes("evidence") && <SidePanelEvidence reports={reports || []} />}
          {cfg.sidePanels.includes("frameworks") && <SidePanelFrameworks frameworks={frameworks || []} />}
          {cfg.sidePanels.includes("vendors") && <SidePanelVendors vendors={vendors || []} />}
        </div>
      </div>
    </div>
  );
}

function QueueRow({ request }) {
  const risk = (request.risk || "med").toUpperCase();
  const riskClass = request.risk === "high" ? "text-risk-high bg-risk-high/10 border-risk-high/30"
                  : request.risk === "low"  ? "text-risk-low  bg-risk-low/10  border-risk-low/30"
                  :                            "text-gold-700 bg-gold-600/10 border-gold-600/30";
  return (
    <li>
      <Link
        to={`/deploy/requests?focus=${request.id}`}
        className="block surface-card-inner p-3 border border-border-subtle rounded-md hover:bg-inset transition-colors"
        data-testid={`role-queue-${request.id}`}
      >
        <div className="flex items-center gap-3 flex-wrap">
          <span className="font-mono text-meta text-ink-secondary tabular-nums">{request.id}</span>
          <span className="text-sm font-semibold text-ink-primary truncate flex-1 min-w-0">{request.agentName}</span>
          <span className={`text-[10px] font-bold uppercase tracking-[0.06em] px-1.5 py-0.5 rounded border ${riskClass}`}>{risk}</span>
          <span className="text-meta text-ink-disabled tabular-nums">{request.stage}/7</span>
        </div>
        <p className="text-meta text-ink-secondary mt-1">
          {request.requestor} · {request.department} · {request.businessUnit}
        </p>
      </Link>
    </li>
  );
}

function SidePanelEvidence({ reports }) {
  const top = reports.slice(0, 4);
  return (
    <PanelCard title="Signed evidence packs" testId="role-side-evidence">
      {top.length === 0 ? (
        <p className="text-meta text-ink-secondary">No evidence archived yet.</p>
      ) : (
        <ul className="space-y-2">
          {top.map((r) => (
            <li key={r.id} className="flex items-start gap-2 text-meta">
              <FileCheck2 size={13} className={r.signed ? "text-risk-low mt-0.5" : "text-ink-disabled mt-0.5"} />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-ink-primary truncate">{r.name}</p>
                <p className="text-meta text-ink-secondary truncate">
                  <span className="font-mono">{r.id}</span> · {r.framework || "—"}
                </p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </PanelCard>
  );
}

function SidePanelFrameworks({ frameworks }) {
  const sorted = [...frameworks].sort((a, b) => (a.coverage || 0) - (b.coverage || 0)).slice(0, 5);
  return (
    <PanelCard title="Framework posture" testId="role-side-frameworks">
      <ul className="space-y-2">
        {sorted.map((f) => (
          <li key={f.id || f.name} className="text-meta">
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-sm font-semibold text-ink-primary truncate">{f.name}</span>
              <span className="text-sm font-bold tabular-nums text-ink-primary">{f.coverage ?? 0}%</span>
            </div>
            <div className="mt-1 h-1.5 rounded-full bg-inset overflow-hidden">
              <div className="h-full bg-tralenex-600 transition-all duration-500" style={{ width: `${f.coverage ?? 0}%` }} />
            </div>
          </li>
        ))}
      </ul>
    </PanelCard>
  );
}

function SidePanelVendors({ vendors }) {
  const top = [...vendors].sort((a, b) => (b.monthlySpendAud || 0) - (a.monthlySpendAud || 0)).slice(0, 5);
  return (
    <PanelCard title="Top vendors by spend" testId="role-side-vendors">
      <ul className="space-y-2">
        {top.map((v) => (
          <li key={v.id} className="flex items-center justify-between gap-3 text-meta">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-ink-primary truncate">{v.name}</p>
              <p className="text-meta text-ink-secondary">{v.agentsGoverned || 0} agents</p>
            </div>
            <span className="text-sm font-bold tabular-nums text-ink-primary shrink-0">{fmtAud(v.monthlySpendAud)}</span>
          </li>
        ))}
      </ul>
    </PanelCard>
  );
}

function RoleSkeleton() {
  return (
    <div className="px-6 lg:px-8 py-6 anim-fade-up">
      <div className="h-8 w-72 rounded-md bg-inset/60 animate-pulse" />
      <div className="mt-2 h-4 w-96 rounded-md bg-inset/40 animate-pulse" />
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-24 rounded-md surface-card animate-pulse bg-inset/30" />
        ))}
      </div>
      <div className="mt-6 h-[280px] rounded-md surface-card animate-pulse bg-inset/20" />
    </div>
  );
}

function RoleError({ message }) {
  return (
    <div className="px-6 lg:px-8 py-6">
      <div className="surface-card shadow-e1 p-5 border-l-[3px] border-l-risk-high">
        <p className="eyebrow text-risk-high">Role view error</p>
        <p className="mt-2 text-meta text-ink-secondary">{message}</p>
      </div>
    </div>
  );
}
